/**
 * 이전 요청 원장 패널 (읽기 전용, 방향 중립 — 이 verse 에서 출발한 요청).
 *
 * 데이터 경계는 전부 `@/client/migration.client` — 이 컴포넌트는 스냅샷을 모르고,
 * 경량 row 만 그린다.
 */

import { useCallback, useEffect, useState } from 'react';
import type { MigrationRequestState } from '@rb/shared/migration/types';
import {
  DEFAULT_PAGE_SIZE,
  MIGRATION_STATES,
  type MigrationLedgerRow,
  type MigrationStateCounts,
  type MigrationStateFilter,
  fetchMigrationCounts,
  fetchMigrationPage,
  searchMigrationsByAddress,
} from '@/client/migration.client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const STATE_FILTERS: MigrationStateFilter[] = ['ALL', ...MIGRATION_STATES];

const STATE_LABEL: Record<MigrationStateFilter, string> = {
  ALL: '전체',
  QUEUED: '대기',
  PROCESSING: '진행',
  DONE: '완료',
  REJECTED: '실패',
};

const STATE_BADGE: Record<
  MigrationRequestState,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  QUEUED: 'outline',
  PROCESSING: 'secondary',
  DONE: 'default',
  REJECTED: 'destructive',
};

function formatTime(ms: number): string {
  if (!ms) return '-';
  return new Date(ms).toLocaleString();
}

/** 주소는 길어서 앞뒤만 남긴다. 전체 값은 title 로 확인·복사할 수 있다. */
function shortAddress(value: string): string {
  if (value.length <= 16) return value || '-';
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function MigrationLedgerPanel() {
  const [stateFilter, setStateFilter] = useState<MigrationStateFilter>('ALL');
  const [rows, setRows] = useState<MigrationLedgerRow[]>([]);
  const [counts, setCounts] = useState<MigrationStateCounts | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  /** 검색이 적용된 주소. null 이면 목록 모드. */
  const [searchedAddress, setSearchedAddress] = useState<string | null>(null);

  const loadFirstPage = useCallback(async (state: MigrationStateFilter) => {
    setLoading(true);
    setError(null);
    try {
      const page = await fetchMigrationPage({ state, limit: DEFAULT_PAGE_SIZE, cursor: null });
      setRows(page.rows);
      setHasMore(page.hasMore);
      setNextCursor(page.nextCursor);
    } catch (e) {
      setError(errorMessage(e));
      setRows([]);
      setHasMore(false);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchedAddress !== null) return;
    void loadFirstPage(stateFilter);
  }, [loadFirstPage, stateFilter, searchedAddress]);

  // 카운터는 목록과 독립 — 실패해도 목록은 살린다(카운트 5회 질의가 더 잘 흔들린다).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const c = await fetchMigrationCounts();
        if (!cancelled) setCounts(c);
      } catch {
        if (!cancelled) setCounts(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLoadMore() {
    if (!hasMore || !nextCursor || loading) return;
    setLoading(true);
    setError(null);
    try {
      const page = await fetchMigrationPage({
        state: stateFilter,
        limit: DEFAULT_PAGE_SIZE,
        cursor: nextCursor,
      });
      setRows((prev) => [...prev, ...page.rows]);
      setHasMore(page.hasMore);
      setNextCursor(page.nextCursor);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    const address = searchInput.trim();
    if (!address) return;
    setLoading(true);
    setError(null);
    try {
      const found = await searchMigrationsByAddress(address);
      setRows(found);
      setSearchedAddress(address);
      setHasMore(false);
      setNextCursor(null);
    } catch (e) {
      setError(errorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  function handleClearSearch() {
    setSearchInput('');
    setSearchedAddress(null);
  }

  const searching = searchedAddress !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">이전 요청</CardTitle>
        <CardDescription>
          이 verse 에서 출발한 `migrationRequests` 원장. 최신 요청순, 읽기 전용입니다.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="migration-counts">
          {counts === null ? (
            <span className="text-xs text-muted-foreground">건수 집계 중…</span>
          ) : (
            STATE_FILTERS.map((s) => (
              <span key={s} className="text-xs text-muted-foreground">
                {STATE_LABEL[s]}{' '}
                <span className="font-mono font-semibold text-foreground">
                  {(s === 'ALL' ? counts.total : counts[s]).toLocaleString()}
                </span>
              </span>
            ))
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {STATE_FILTERS.map((s) => (
            <Button
              key={s}
              type="button"
              size="sm"
              variant={stateFilter === s ? 'default' : 'outline'}
              disabled={searching}
              onClick={() => setStateFilter(s)}
            >
              {STATE_LABEL[s]}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="max-w-md font-mono text-xs"
            placeholder="주소로 검색 (출발 P 또는 도착 A 주소)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSearch();
            }}
          />
          <Button type="button" size="sm" onClick={() => void handleSearch()} disabled={loading}>
            검색
          </Button>
          {searching && (
            <Button type="button" size="sm" variant="outline" onClick={handleClearSearch}>
              검색 해제
            </Button>
          )}
          {searching && (
            <span className="text-xs text-muted-foreground">
              `{searchedAddress}` 검색 결과 — 상태 필터·페이지네이션은 해제 후 사용하세요.
            </span>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              <pre className="whitespace-pre-wrap break-all font-mono text-xs">{error}</pre>
            </AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>요청 시각</TableHead>
              <TableHead>닉네임</TableHead>
              <TableHead>출발 (P)</TableHead>
              <TableHead>도착 (A)</TableHead>
              <TableHead>상태</TableHead>
              <TableHead>사유</TableHead>
              <TableHead>migrationId</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-sm text-muted-foreground">
                  표시할 이전 요청이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.migrationId}>
                  <TableCell className="whitespace-nowrap text-xs">
                    {formatTime(r.createdAt)}
                  </TableCell>
                  <TableCell className="text-xs">{r.nickname ?? '-'}</TableCell>
                  <TableCell className="font-mono text-xs" title={r.P}>
                    {shortAddress(r.P)}
                  </TableCell>
                  <TableCell className="font-mono text-xs" title={r.A}>
                    {shortAddress(r.A)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATE_BADGE[r.state] ?? 'outline'}>
                      {STATE_LABEL[r.state] ?? r.state}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-xs',
                      r.reason ? 'text-destructive' : 'text-muted-foreground'
                    )}
                  >
                    {r.reason ?? '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs" title={r.migrationId}>
                    {shortAddress(r.migrationId)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div className="flex items-center gap-3">
          {!searching && hasMore && (
            <Button type="button" size="sm" variant="outline" onClick={() => void handleLoadMore()}>
              더 보기
            </Button>
          )}
          {loading && <span className="text-xs text-muted-foreground">불러오는 중…</span>}
          <span className="text-xs text-muted-foreground">{rows.length}건 표시 중</span>
        </div>
      </CardContent>
    </Card>
  );
}
